import type { PreOrderData } from '@/lib/checkout/preOrder';
import { getPreOrderChargeAmount } from '@/lib/checkout/preOrderSummary';
import type { Item } from './types';

export type PreOrderTransactionType = 'DEPOSIT' | 'BALANCE' | 'FULL';

export function mapProductTypeForGtm(productType?: string): 'watch' | 'strap' {
  return productType?.toLowerCase() === 'strap' ? 'strap' : 'watch';
}

export function isPreOrderCta(ctaType?: string | null): boolean {
  return ctaType?.toUpperCase().replaceAll('-', '_') === 'PRE_ORDER';
}

export function buildPreOrderItemExtras(params: {
  ctaType?: string;
  productType?: string;
  itemListName?: string;
  catalogMode?: boolean;
}): Record<string, string> {
  const extras: Record<string, string> = {};
  if (params.ctaType) extras.cta_type = params.ctaType;
  if (isPreOrderCta(params.ctaType)) extras.order_type = 'PRE_ORDER';
  if (params.productType) extras.product_type = mapProductTypeForGtm(params.productType);
  if (params.itemListName) extras.item_list_name = params.itemListName;
  if (params.catalogMode) extras.catalog_mode = 'true';
  return extras;
}

/**
 * Stamp `order_type` onto ecommerce items for pre-order SKUs.
 *
 * Example Input:
 * items=[{item_id:'ABC'}], preOrderSkus=['ABC']
 *
 * Example Output:
 * [{item_id:'ABC', order_type:'PRE_ORDER'}]
 */
export function stampPreOrderItems(
  items: Item[] | undefined,
  preOrderSkus?: ReadonlyArray<string> | ReadonlySet<string>
): Item[] | undefined {
  if (!items?.length) return items;

  if (!preOrderSkus) {
    return items.map((item) => ({ ...item, order_type: 'PRE_ORDER' as const }));
  }

  const skuSet = new Set(preOrderSkus instanceof Set ? [...preOrderSkus] : preOrderSkus);
  if (skuSet.size === 0) return items;

  return items.map((item) => {
    const rawId = item.item_id?.replaceAll('_', '.');
    const matches = skuSet.has(item.item_id) || (rawId !== undefined && skuSet.has(rawId));
    return matches ? { ...item, order_type: 'PRE_ORDER' as const } : item;
  });
}

export function buildPreOrderCheckoutExtras(params: {
  preOrderData: PreOrderData;
  checkoutTotal: number;
  showTax: boolean;
}) {
  const deposit = params.showTax ? params.preOrderData.deposit : params.preOrderData.deposit_net;
  return {
    order_type: 'PRE_ORDER' as const,
    transaction_type: 'DEPOSIT' as const,
    deposit_amount: deposit,
    balance_amount: params.checkoutTotal - deposit,
  };
}

export function buildPreOrderPurchaseEventExtras(params: {
  transactionType: PreOrderTransactionType;
  preOrderData: PreOrderData;
  checkoutTotal: number;
  showTax: boolean;
}): Record<string, string | number> {
  const { deposit_amount, balance_amount, order_type } = buildPreOrderCheckoutExtras({
    preOrderData: params.preOrderData,
    checkoutTotal: params.checkoutTotal,
    showTax: params.showTax,
  });

  return {
    order_type,
    transaction_type: params.transactionType,
    deposit_amount,
    balance_amount,
  };
}

export function buildPreOrderPageExtras(params: {
  orderType?: string;
  paymentStatus?: string;
  balanceRemaining?: number;
}): Record<string, string | number> {
  const extras: Record<string, string | number> = {};
  if (params.orderType === 'PRE_ORDER') extras.order_type = 'PRE_ORDER';
  if (params.paymentStatus) extras.payment_status = params.paymentStatus;
  if (params.balanceRemaining !== undefined) extras.balance_remaining = params.balanceRemaining;
  return extras;
}

export function resolvePreOrderPurchase(params: {
  transactionType: PreOrderTransactionType;
  preOrderData: PreOrderData | null;
  chargedAmount: number;
  showTax?: boolean;
  checkoutTotal?: number;
}) {
  if (params.transactionType === 'FULL' || !params.preOrderData) {
    return {
      value: params.chargedAmount,
      transaction_type: 'FULL' as const,
      omitLineItems: false,
    };
  }

  const isDeposit = params.transactionType === 'DEPOSIT';
  const showTax = params.showTax ?? true;
  const depositApplied = showTax ? params.preOrderData.deposit : params.preOrderData.deposit_net;
  const value = isDeposit
    ? getPreOrderChargeAmount(params.preOrderData, showTax)
    : (params.checkoutTotal ?? params.chargedAmount) - depositApplied;

  return {
    value,
    transaction_type: params.transactionType,
    omitLineItems: true,
    order_type: 'PRE_ORDER' as const,
  };
}

export function isPreOrderFinancingAnalyticsAllowed(ctaType?: string): boolean {
  return ctaType !== 'PRE_ORDER';
}
